// ModeRules.js
// Read-only "how to play" page for a game mode. Reached by tapping a card on
// the Roster Battles screen (pickup / knockout) or the Dynasty nav tab.
// Shows the mode's rules and an "iOS only" button that opens the App Store —
// nothing is playable on web yet. Rules text mirrors the iOS overview screens.

import React from "react";
import { useNavigate } from "react-router-dom";
import strategyIcon from "../assets/strategy-fill.svg";
import treeIcon from "../assets/tree-structure.svg";
import crownIcon from "../assets/crown-simple-fill.svg";

const APP_STORE_URL = "https://apps.apple.com/app/rosterable/id6761739479";

// Mode definitions. Rules are taken verbatim from the iOS overview screens
// (PickUpOverviewView / KnockoutOverviewView); Dynasty is summarized from
// DynastyView since it has no single rules card in the app.
const MODES = {
  pickup: {
    title: "PICK-UP DRAFT",
    subtitle: "Head to Head",
    accent: "#00BFB8",
    icon: strategyIcon,
    rules: [
      "Head-to-head drafts vs an opponent",
      "Draft from the same pool of available players",
      "Snake draft order",
      "24-hour max timer per pick",
      "Forfeit on consecutive missed picks",
    ],
  },
  knockout: {
    title: "KNOCKOUT MODE",
    subtitle: "Survival Leagues",
    accent: "#A68CF2",
    icon: treeIcon,
    iconRotation: 180,
    rules: [
      "All users draft from an identical player pool",
      "90-second timer to complete your draft",
      "No Rating Reveal or Player Refresh",
      "Top 50% of scores advance to the next day",
      "Ends with a 1v1 final to crown a champion",
      "New pools begin every Saturday",
    ],
  },
  dynasty: {
    title: "DYNASTY",
    subtitle: "Build Your Franchise",
    accent: "#d4f53c",
    icon: crownIcon,
    rules: [
      "Draft a founding roster to start your dynasty",
      "Complete daily challenges: Streak, Ladder, and Lineup Links",
      "Each challenge has Bronze, Silver, and Gold tiers",
      "Clear Bronze and Silver to unlock Gold",
      "Earn reward packs to upgrade your roster over time",
    ],
  },
};

export default function ModeRules({ modeKey }) {
  const navigate = useNavigate();
  const mode = MODES[modeKey];
  if (!mode) return null;

  const { title, subtitle, accent, icon, iconRotation = 0, rules } = mode;

  return (
    <div style={styles.container}>
      {/* Back */}
      <button style={styles.backBtn} onClick={() => navigate(-1)}>‹</button>

      {/* Header */}
      <div style={styles.header}>
        <div style={{ ...styles.iconChip, background: accent }}>
          <img
            src={icon}
            alt=""
            style={{
              width: 40,
              height: 40,
              filter: "brightness(0)",
              opacity: 0.85,
              transform: `rotate(${iconRotation}deg)`,
            }}
          />
        </div>
        <span style={styles.title}>{title}</span>
        <span style={styles.subtitle}>{subtitle}</span>
      </div>

      {/* How to play */}
      <div style={styles.rulesCard}>
        <div style={styles.sectionRule}>
          <div style={{ ...styles.rule, background: `linear-gradient(to left, ${accent}8c, transparent)` }} />
          <span style={{ ...styles.sectionLabel, color: accent }}>HOW TO PLAY</span>
          <div style={{ ...styles.rule, background: `linear-gradient(to right, ${accent}8c, transparent)` }} />
        </div>

        <div style={styles.ruleList}>
          {rules.map((text, i) => (
            <div key={i} style={styles.ruleRow}>
              <span style={{ ...styles.ruleDot, background: accent }} />
              <span style={styles.ruleText}>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* iOS-only CTA */}
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...styles.iosButton, background: accent }}
      >
        Currently only available on iOS app version
      </a>
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    padding: "8px 20px 32px",
    display: "flex",
    flexDirection: "column",
  },
  backBtn: {
    alignSelf: "flex-start",
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.8)",
    fontSize: "28px",
    fontWeight: "300",
    lineHeight: 1,
    padding: "4px 10px 4px 0",
    cursor: "pointer",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "12px 0 24px",
  },
  iconChip: {
    width: "68px",
    height: "68px",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "26px",
    fontWeight: "900",
    fontStyle: "italic",
    color: "rgba(255,255,255,0.92)",
    letterSpacing: "3px",
    textAlign: "center",
  },
  subtitle: {
    fontSize: "14px",
    fontWeight: "500",
    fontStyle: "italic",
    color: "rgba(255,255,255,0.45)",
  },
  rulesCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "14px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  sectionRule: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  rule: {
    flex: 1,
    height: "1px",
  },
  sectionLabel: {
    fontSize: "12px",
    fontWeight: "800",
    letterSpacing: "2.5px",
    whiteSpace: "nowrap",
  },
  ruleList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  ruleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  ruleDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  ruleText: {
    fontSize: "14px",
    fontWeight: "400",
    color: "rgba(255,255,255,0.7)",
  },
  iosButton: {
    marginTop: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    height: "52px",
    borderRadius: "14px",
    color: "#000",
    fontSize: "14px",
    fontWeight: "800",
    letterSpacing: "0.3px",
    textDecoration: "none",
    padding: "0 16px",
  },
};
